import { useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark, faArrowUp, faArrowDown } from "@fortawesome/free-solid-svg-icons";
import styles from "../WorkoutAdminModal.module.css";
import { useFocusTrap } from "../../hooks/useFocusTrap";

const METHOD_SUGGESTIONS = ["PPL", "ABC", "ABCD", "ABCDE", "Full body", "Upper/Lower", "Push/Pull"];
const LETTERS = "ABCDEFGHIJKLMN";

/**
 * Criar / editar programa (pasta de treinos por método). Define nome, método,
 * descrição e quais treinos fazem parte, na ordem em que são feitos (A, B, C).
 * Um treino fica em um programa só: escolher aqui tira ele de outro programa.
 */
export default function ProgramModal({ program, availablePlans, programOfPlan, onClose, onSave, onDelete }) {
  const initial = useRef({
    name: program?.name || "",
    method: program?.method || "",
    description: program?.description || "",
    planIds: (program?.plans || []).map((p) => p.id),
  });
  const [name, setName] = useState(initial.current.name);
  const [method, setMethod] = useState(initial.current.method);
  const [description, setDescription] = useState(initial.current.description);
  const [planIds, setPlanIds] = useState(initial.current.planIds);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [nameError, setNameError] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const dialogRef = useRef(null);
  const nameRef = useRef(null);

  const dirty =
    name !== initial.current.name ||
    method !== initial.current.method ||
    description !== initial.current.description ||
    planIds.join(",") !== initial.current.planIds.join(",");

  // Fechar sem querer (clique fora, Esc) não pode jogar fora um programa montado
  const requestClose = () => {
    if (saving) return;
    if (dirty) { setConfirmDiscard(true); return; }
    onClose();
  };
  useFocusTrap(dialogRef, true, requestClose);

  const plansById = useMemo(() => new Map(availablePlans.map((p) => [p.id, p])), [availablePlans]);
  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return availablePlans
      .filter((p) => !planIds.includes(p.id))
      .filter((p) => !q || (p.title || "").toLowerCase().includes(q));
  }, [availablePlans, planIds, query]);

  const move = (idx, delta) => {
    setPlanIds((prev) => {
      const next = [...prev];
      const j = idx + delta;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) {
      setNameError(true);
      nameRef.current?.focus();
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSave(program?.id || null, { name: name.trim(), method: method.trim(), description: description.trim() }, planIds);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || "Não foi possível salvar o programa.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await onDelete(program.id);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || "Não foi possível excluir o programa.");
      setSaving(false);
      setConfirmDelete(false);
    }
  };

  const inUse = program?.students_in_use || 0;
  const titleOf = (id) => plansById.get(id)?.title || `Treino #${id}`;

  return (
    <div className={styles.innerOverlay} onClick={requestClose}>
      <div
        ref={dialogRef}
        className={styles.planModal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="wam-program-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.planModalHeader}>
          <div>
            <h3 id="wam-program-title">{program ? "Editar programa" : "Novo programa"}</h3>
            <p>Reúna os treinos de um mesmo método, na ordem em que o aluno faz.</p>
          </div>
          <button type="button" className={styles.planModalClose} onClick={requestClose} aria-label="Fechar" disabled={saving}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <form className={styles.wamForm} onSubmit={handleSubmit} noValidate>
          {inUse > 0 && (
            <p className={styles.programInUse}>
              Em uso por <strong>{inUse} {inUse === 1 ? "aluno" : "alunos"}</strong>. As mudanças valem para as próximas atribuições: os treinos que eles já receberam não mudam.
            </p>
          )}
          <div className={styles.wamFormGrid}>
            <div className={styles.wamField}>
              <label htmlFor="program-name">Nome</label>
              <input id="program-name" ref={nameRef} type="text" value={name} maxLength={120}
                     aria-invalid={nameError || undefined}
                     aria-describedby={nameError ? "program-name-error" : undefined}
                     onChange={(e) => { setName(e.target.value); if (nameError) setNameError(false); }}
                     placeholder="Ex: Push Pull Legs - Hipertrofia" />
              {nameError && (
                <p id="program-name-error" className={styles.programError}>Dê um nome ao programa, com pelo menos 2 letras.</p>
              )}
            </div>
            <div className={styles.wamField}>
              <label htmlFor="program-method">Método <span className={styles.programOptional}>(opcional)</span></label>
              <input id="program-method" type="text" value={method} maxLength={60} list="program-method-options"
                     onChange={(e) => setMethod(e.target.value)} placeholder="Ex: PPL" />
              <datalist id="program-method-options">
                {METHOD_SUGGESTIONS.map((m) => <option key={m} value={m} />)}
              </datalist>
            </div>
          </div>

          <div className={styles.wamField}>
            <label htmlFor="program-desc">Descrição <span className={styles.programOptional}>(opcional)</span></label>
            <textarea id="program-desc" rows="2" value={description} maxLength={2000}
                      onChange={(e) => setDescription(e.target.value)} placeholder="Objetivo, fase, observações" />
          </div>

          <div className={styles.wamField}>
            <label id="program-order-label">
              Treinos do programa, na ordem {planIds.length > 0 && <span className={styles.programOptional}>({planIds.length})</span>}
            </label>
            {planIds.length === 0 ? (
              <p className={styles.programHint}>Nenhum treino ainda. Escolha abaixo, na ordem em que o aluno faz.</p>
            ) : (
              <ol className={styles.programOrderList} aria-labelledby="program-order-label">
                {planIds.map((id, idx) => (
                  <li key={id} className={styles.programOrderRow}>
                    <span className={styles.programLetter} aria-hidden="true">{LETTERS[idx] || idx + 1}</span>
                    <span className={styles.programOrderTitle}>{titleOf(id)}</span>
                    <button type="button" className={`${styles.wamBtn} ${styles.smallGhost}`} onClick={() => move(idx, -1)}
                            disabled={idx === 0} aria-label={`Subir ${titleOf(id)}`}>
                      <FontAwesomeIcon icon={faArrowUp} />
                    </button>
                    <button type="button" className={`${styles.wamBtn} ${styles.smallGhost}`} onClick={() => move(idx, 1)}
                            disabled={idx === planIds.length - 1} aria-label={`Descer ${titleOf(id)}`}>
                      <FontAwesomeIcon icon={faArrowDown} />
                    </button>
                    <button type="button" className={`${styles.wamBtn} ${styles.smallGhost}`}
                            aria-label={`Tirar ${titleOf(id)} do programa`}
                            onClick={() => setPlanIds((prev) => prev.filter((x) => x !== id))}>
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </li>
                ))}
              </ol>
            )}
          </div>

          <div className={styles.wamField}>
            <label htmlFor="program-add">Adicionar treino</label>
            <input id="program-add" type="search" value={query}
                   onChange={(e) => setQuery(e.target.value)} placeholder="Buscar entre os seus treinos" />
            <div className={styles.programCandidates}>
              {candidates.slice(0, 40).map((p) => {
                const other = programOfPlan.get(p.id);
                return (
                  <button key={p.id} type="button" className={styles.programCandidate}
                          onClick={() => setPlanIds((prev) => [...prev, p.id])}>
                    <span>+ {p.title}</span>
                    {other && other.id !== program?.id && (
                      <span className={styles.programHint}>sai de "{other.name}"</span>
                    )}
                  </button>
                );
              })}
              {!candidates.length && (
                <p className={styles.programHint}>
                  {availablePlans.length ? "Nenhum treino encontrado." : "Crie seus treinos primeiro, em \"Criar treino\"."}
                </p>
              )}
            </div>
          </div>

          {error && <p className={styles.programError} role="alert">{error}</p>}

          {confirmDiscard ? (
            <div className={styles.programConfirm} role="alert">
              <p>Descartar as alterações deste programa?</p>
              <div className={styles.actionsRow}>
                <button type="button" className={`${styles.wamBtn} ${styles.ghost}`} onClick={() => setConfirmDiscard(false)}>
                  Continuar editando
                </button>
                <button type="button" className={`${styles.wamBtn} ${styles.danger}`} onClick={onClose}>
                  Descartar
                </button>
              </div>
            </div>
          ) : confirmDelete ? (
            <div className={styles.programConfirm} role="alert">
              <p>
                Excluir o programa "{program.name}"? Os treinos continuam existindo, como avulsos, e o histórico dos alunos não muda.
                {inUse > 0 && ` ${inUse} ${inUse === 1 ? "aluno continua" : "alunos continuam"} com os treinos que já receberam.`}
              </p>
              <div className={styles.actionsRow}>
                <button type="button" className={`${styles.wamBtn} ${styles.ghost}`} onClick={() => setConfirmDelete(false)} disabled={saving}>
                  Manter programa
                </button>
                <button type="button" className={`${styles.wamBtn} ${styles.danger}`} onClick={handleDelete} disabled={saving}>
                  Excluir programa
                </button>
              </div>
            </div>
          ) : (
            <div className={`${styles.actionsRow} ${styles.programActions}`}>
              {program && (
                <button type="button" className={`${styles.wamBtn} ${styles.smallDanger}`} onClick={() => setConfirmDelete(true)} disabled={saving}>
                  Excluir
                </button>
              )}
              <span className={styles.programActionsSpacer} />
              <button type="button" className={`${styles.wamBtn} ${styles.ghost}`} onClick={requestClose} disabled={saving}>
                Cancelar
              </button>
              <button type="submit" className={`${styles.wamBtn} ${styles.primary}`} disabled={saving}>
                {program ? "Salvar programa" : "Criar programa"}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
